import { useState, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useRouter } from "expo-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { step2Schema, type Step2Values } from "../schemas/preVettingSchema";
import { usePreVettingDraft } from "./usePreVettingDraft";
import { useDebouncedDraftSave } from "./useDebouncedDraftSave";
import { useRestoredDocumentFile } from "@/hooks/useRestoredDocumentFile";
import { preVettingApi } from "@/lib/api/preVetting";
import type { FileUploadState, SelectedFile } from "@/components/ui/FileUpload";
import type { TranslationKeys } from "@chinese-vendor/i18n";

export function usePreVettingStep2Form() {
  const router = useRouter();
  const { draftQuery, saveDraftMutation } = usePreVettingDraft();
  const scheduleSave = useDebouncedDraftSave(saveDraftMutation.mutateAsync);

  const form = useForm<Step2Values>({
    resolver: zodResolver(step2Schema),
    defaultValues: {
      company_name_chinese: "",
      company_name_english: "",
      company_name_arabic: "",
      hq_address: "",
      hq_city: "",
      hq_postal_code: "",
      unified_social_credit_code: "",
      business_license_document: null,
    },
    mode: "onBlur",
  });

  const [businessLicenseFile, setBusinessLicenseFile] = useState<SelectedFile | null>(null);
  const [businessLicenseUploadState, setBusinessLicenseUploadState] =
    useState<FileUploadState>("idle");
  const [businessLicenseUploadError, setBusinessLicenseUploadError] = useState<
    TranslationKeys | undefined
  >(undefined);

  // Restore file preview from saved S3 key on draft load
  const { file: restoredBizLicense, uploadState: restoredBizLicenseState } =
    useRestoredDocumentFile(
      draftQuery.isSuccess ? (draftQuery.data?.step2?.business_license_document ?? null) : null
    );

  useEffect(() => {
    if (restoredBizLicense && businessLicenseUploadState === "idle" && !businessLicenseFile) {
      setBusinessLicenseFile(restoredBizLicense);
      setBusinessLicenseUploadState(restoredBizLicenseState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoredBizLicense]);

  // Populate form once draft has loaded
  useEffect(() => {
    if (draftQuery.isSuccess && draftQuery.data?.step2) {
      form.reset({
        ...form.getValues(),
        ...(draftQuery.data.step2 as Partial<Step2Values>),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftQuery.isSuccess]);

  const onFieldBlur = useCallback(() => {
    scheduleSave({ step2: form.getValues() });
  }, [scheduleSave, form]);

  const handleBusinessLicenseFile = useCallback(
    async (file: SelectedFile) => {
      setBusinessLicenseFile(file);
      setBusinessLicenseUploadState("uploading");
      setBusinessLicenseUploadError(undefined);
      try {
        const key = await preVettingApi.uploadDocument(file);
        form.setValue("business_license_document", key);
        onFieldBlur();
        setBusinessLicenseUploadState("success");
      } catch {
        setBusinessLicenseUploadState("error");
        setBusinessLicenseUploadError("fileUpload.uploadFailed");
      }
    },
    [form, onFieldBlur]
  );

  const handleBusinessLicenseRemove = useCallback(() => {
    setBusinessLicenseFile(null);
    setBusinessLicenseUploadState("idle");
    setBusinessLicenseUploadError(undefined);
    form.setValue("business_license_document", null);
  }, [form]);

  const handleBack = useCallback(() => {
    router.back();
  }, [router]);

  const handleNext = form.handleSubmit(async (values) => {
    await saveDraftMutation.mutateAsync({ step2: values });
    router.push("/(vendor)/pre-vetting/step-3");
  });

  return {
    form,
    onFieldBlur,
    handleNext,
    handleBack,
    businessLicenseFile,
    businessLicenseUploadState,
    businessLicenseUploadError,
    handleBusinessLicenseFile,
    handleBusinessLicenseRemove,
  };
}
